import type { RoomStockObjectInput } from "@/lib/gql/__generated__/graphql";

import { stockRemainNumZodSchema } from "@/components/features/_shared/validation-schema/validation-schema";

import type { ServerModel } from "./type";

// Action の型定義
type ActionValue = {
	roomID: string;
	date: string;
	isAvailable: boolean;
	remainNum: number;
};

export class RoomStockDiffStore {
	#diffState: ActionValue[] = [];

	constructor(
		private readonly roomStockObjects: ServerModel["roomStock"]["items"], //
		private readonly onSetIsValid: (isValid: boolean) => void,
		private readonly onSetIsDirty: (isDirty: boolean) => void,
	) {}

	/**
	 * 更新のあったデータを追加
	 * @param value
	 */
	readonly put = (value: ActionValue) => {
		this.#diffState = this.#diffState.filter((stateItem) => !this.#isSameActionValue(stateItem, value));
		this.#diffState.push(value);

		// render中に状態更新処理が走る場合があるので、遅延させている
		setTimeout(() => {
			this.onSetIsValid(this.isValid());
			this.onSetIsDirty(this.isDirty());
		}, 0);
	};

	readonly clear = () => {
		this.#diffState.length = 0;
	};

	readonly isDirty = () => {
		if (!this.#diffState.length) return false;

		const diffItems = this.#diffState;
		const roomStockObjects = this.roomStockObjects;

		const isDirty = diffItems.some((diffItem) => {
			const roomStockObject = roomStockObjects.find((roomStockObject) => {
				return roomStockObject.roomID === diffItem.roomID;
			});

			if (!roomStockObject) return false;

			const sameStockItem = roomStockObject.stocks.find((stock) => stock.date === diffItem.date);

			if (!sameStockItem) return false;

			return sameStockItem.remainNum !== diffItem.remainNum || sameStockItem.isAvailable !== diffItem.isAvailable;
		});

		return isDirty;
	};

	readonly isValid = () => {
		return this.#diffState.every((item) => stockRemainNumZodSchema.safeParse(item.remainNum).success);
	};

	readonly getCurrentRoomStock = (value: ActionValue) => {
		return this.#diffState.find((item) => this.#isSameActionValue(item, value));
	};

	/**
	 * サーバーに送信するデータを取得
	 * @returns
	 */
	readonly getDiffItems = (): Array<RoomStockObjectInput> => {
		const roomIDs = this.#diffState.map((item) => item.roomID);
		const notDuplicateRoomIDs = Array.from(new Set(roomIDs));

		const diffItems = notDuplicateRoomIDs
			.map((roomID) => {
				const stocks = this.#diffState
					.filter((stateItem) => stateItem.roomID === roomID) // 特定のプランにひもづくデータを取得
					.map((item) => ({
						date: item.date,
						remainNum: item.remainNum,
						isAvailable: item.isAvailable,
					}));
				return {
					roomID,
					stocks,
				};
			})
			.flat();

		return diffItems;
	};

	#isSameActionValue = (a: ActionValue, b: ActionValue) => {
		if (a.roomID !== b.roomID) {
			return false;
		}

		if (a.date !== b.date) {
			return false;
		}

		return true;
	};
}
